import { Chart } from "chart.js";
import { useEffect, useRef, useState } from "react";
import theme from "../../../UI/theme";
import { DateTime } from "luxon";
import { getDateTime } from "../../../scripts/log";

const chartData = {
    labels: [],
    datasets: [
        {
            label: 'Ambience %',
            type: 'line',
            borderColor: `${theme.colors.raspberryRed}`,
            backgroundColor: `${theme.colors.raspberryRed}`,
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.5,
            fill: false,
            order: 2,
            data: [],
        },
        {
            label: 'Music %',
            type: 'line',
            borderColor: `${theme.colors.mellowYellow}`,
            backgroundColor: `${theme.colors.mellowYellow}`,
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.5,
            fill: false,
            order: 1,
            data: [],
        },
        {
            label: 'Measured dB',
            type: 'line',
            borderColor: `${theme.colors.crystalBlue}`,
            backgroundColor: `${theme.colors.crystalBlue}`,
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.5,
            fill: false,
            order: 3,
            data: [],
            hidden: true
        }]
}

const signalLevelDataset = {
    label: 'Signal level',
    type: 'line',
    borderColor: `${theme.colors.blueMonday}`,
    backgroundColor: `${theme.colors.blueMonday}`,
    borderWidth: 2,
    pointRadius: 0,
    tension: 0.5,
    fill: false,
    order: 3,
    data: [],
    hidden: true
}

const commonOptions = {
    maintainAspectRatio: false,
    animation: {
        duration: 0,
    }
}

const options = {
    ...commonOptions,
    layout: {
        padding: 0
    },
    scales: {
        x: {
            display: false,
            type: 'time',
        },
        y: {
            display: false,
            type: 'linear',
            suggestedMax: 50,
            suggestedMin: 30,
        }
    },
    plugins: {
        legend: {
            display: false
        },
        tooltip: {
            enabled: false
        }
    },
    pointHitRadius: 7,
}

const detailedOptions = {
    ...commonOptions,
    scales: {
        x: {
            display: true,
            type: 'time',
            grid: {
                display: true,
                drawOnChartArea: false,
                drawBorder: true,
                borderColor: theme.colors.darkSpace70,
                drawTicks: true,
                tickColor: theme.colors.darkSpace70
            },
            ticks: {
                color: theme.colors.textGray
            }
        },
        y: {
            display: true,
            type: 'linear',
            min: 0,
            suggestedMax: 100,
            grid: {
                display: true,
                drawOnChartArea: false,
                drawBorder: true,
                borderColor: theme.colors.darkSpace70,
                drawTicks: true,
                tickColor: theme.colors.darkSpace70
            },
            ticks: {
                color: theme.colors.textGray
            }
        }
    },
    plugins: {
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                usePointStyle: true,
                pointStyle: 'rectRounded',
                color: theme.colors.textGray,
                boxWidth: 7,
                boxHeight: 7,
                useBorderRadius: true,
                borderRadius: 1,
            }
        },
        tooltip: {
            enabled: true
        }
    },
    pointHitRadius: 7,
}

const LiveChart = props => {
    const chartContainer = useRef(null);
    const [chartInstance, setChartInstance] = useState(null);
    let chartConfig;
    if (props.detailed) {
        chartConfig = JSON.parse(JSON.stringify({ data: chartData, options: { ...detailedOptions } }));
        chartConfig.data.datasets[2].hidden = false;
    } else {
        chartConfig = JSON.parse(JSON.stringify({ data: chartData, options: { ...options } }));
    }

    if (props.showSignalLevel && chartConfig.data.datasets.length === 3) {
        chartConfig.data.datasets.push(signalLevelDataset);
    }

    const chartConfigRef = useRef(JSON.parse(JSON.stringify(chartConfig)));

    useEffect(() => {
        chartConfigRef.current.data.datasets[0].data = props.data?.map(value => ({ x: value.timestamp, y: value.noise > 0 ? value.noise : null }));
        chartConfigRef.current.data.datasets[1].data = props.data?.map(value => ({ x: value.timestamp, y: value.sysvol }));
        chartConfigRef.current.data.datasets[2].data = props.data?.map(value => ({ x: value.timestamp, y: value.averageDecibel > 0 ? value.averageDecibel : null }));
        if (chartConfigRef.current.data.datasets.length === 4) {
            chartConfigRef.current.data.datasets[3].data = props.data?.map(value => ({ x: value.timestamp, y: value.meterDb }));
        }

        if (chartInstance) {
            chartInstance.update();
        }
    }, [props.data])

    useEffect(() => {
        if (chartContainer && chartContainer.current) {
            if (chartInstance) {
                chartInstance.destroy();
            }
            const newChartInstance = new Chart(chartContainer.current, chartConfigRef.current);
            setChartInstance(newChartInstance);
        }
    }, [chartContainer]);

    return (
        <canvas ref={chartContainer} />
    );
}

export default LiveChart;